import React, { useState, useEffect, useCallback } from 'react';
import { researchAPI, profilesAPI } from '../services/api';
import { useToast } from './Toast';
import '../styles/WorkspaceChrome.css';
import './ResearchWizard.css';

const GROUP_LABELS = {
  workspace: 'Draft workspaces',
  publishing: 'Publishing queue',
  published: 'Published',
};

const emptyBrief = () => ({
  name: '',
  niche: '',
  description: '',
  target_keywords: '',
  competitor_urls: '',
});

function ResearchWizard({ onReportReady, initialView = 'wizard' }) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(initialView);
  const [step, setStep] = useState(1);
  const [running, setRunning] = useState(false);
  const [foundation, setFoundation] = useState('brief');
  const [briefForm, setBriefForm] = useState(emptyBrief);
  const [contentMode, setContentMode] = useState('draft');
  const [selectedDraftId, setSelectedDraftId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [contentSources, setContentSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [saveRecurring, setSaveRecurring] = useState({
    enabled: false,
    name: '',
    research_frequency: 'weekly',
    auto_reports_enabled: false,
  });
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [manageLoading, setManageLoading] = useState(false);
  const [triggeringId, setTriggeringId] = useState(null);

  const maxStep = foundation === 'brief' ? 4 : 3;

  const loadContentSources = useCallback(async () => {
    try {
      setSourcesLoading(true);
      const res = await researchAPI.getContentSources();
      setContentSources(res.data.items || []);
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Failed to load content',
        'error'
      );
    } finally {
      setSourcesLoading(false);
    }
  }, [showToast]);

  const loadSavedProfiles = useCallback(async () => {
    try {
      setManageLoading(true);
      const res = await profilesAPI.getProfiles();
      setSavedProfiles(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Failed to load saved research', 'error');
    } finally {
      setManageLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (open && foundation === 'existing_content' && step === 2) {
      void loadContentSources();
    }
  }, [open, foundation, step, loadContentSources]);

  useEffect(() => {
    if (open && view === 'manage') {
      void loadSavedProfiles();
    }
  }, [open, view, loadSavedProfiles]);

  useEffect(() => {
    if (initialView === 'manage') {
      setView('manage');
      setOpen(true);
    }
  }, [initialView]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Wizard / manage dialogs require explicit Cancel or close — no Escape dismiss.
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const resetWizard = () => {
    setView('wizard');
    setStep(1);
    setFoundation('brief');
    setBriefForm(emptyBrief());
    setContentMode('draft');
    setSelectedDraftId('');
    setExternalUrl('');
    setSaveRecurring({
      enabled: false,
      name: '',
      research_frequency: 'weekly',
      auto_reports_enabled: false,
    });
  };

  const openWizard = () => {
    resetWizard();
    setOpen(true);
  };

  const openManage = () => {
    setView('manage');
    setOpen(true);
  };

  const closeModal = () => {
    if (running) return;
    setOpen(false);
    resetWizard();
  };

  const pollUntilReportHasIdeas = (reportId, successMessage) => {
    const pollInterval = setInterval(async () => {
      try {
        const reportResponse = await researchAPI.getReport(reportId);
        const report = reportResponse.data;
        if (report.article_ideas && report.article_ideas.length > 0) {
          clearInterval(pollInterval);
          setRunning(false);
          showToast(successMessage, 'success');
          closeModal();
          if (onReportReady) onReportReady(reportId);
        }
      } catch (err) {
        console.log('Polling for report completion...', err.response?.status);
      }
    }, 3000);

    setTimeout(() => {
      clearInterval(pollInterval);
      setRunning(false);
    }, 300000);
  };

  const buildPayload = () => {
    if (foundation === 'brief') {
      const keywords = briefForm.target_keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      const urls = briefForm.competitor_urls
        .split(/[,\n]/)
        .map((u) => u.trim())
        .filter(Boolean);
      const payload = {
        foundation: 'brief',
        brief: {
          name: briefForm.name.trim() || undefined,
          niche: briefForm.niche.trim() || undefined,
          description: briefForm.description.trim() || undefined,
          target_keywords: keywords,
          competitor_urls: urls,
        },
        save_recurring: saveRecurring.enabled
          ? {
              enabled: true,
              name: (saveRecurring.name || briefForm.name).trim(),
              research_frequency: saveRecurring.research_frequency,
              auto_reports_enabled: saveRecurring.auto_reports_enabled,
            }
          : { enabled: false },
      };
      return payload;
    }
    return {
      foundation: 'existing_content',
      existing_content:
        contentMode === 'draft'
          ? { source_type: 'draft', draft_id: Number(selectedDraftId) }
          : { source_type: 'external_url', url: externalUrl.trim() },
    };
  };

  const validateStep = (s) => {
    if (s === 1) return true;
    if (s === 2 && foundation === 'brief') {
      const has =
        briefForm.name.trim() ||
        briefForm.target_keywords.trim() ||
        briefForm.description.trim();
      if (!has) {
        showToast('Add a name, keywords, or audience description.', 'warning');
        return false;
      }
      return true;
    }
    if (s === 2 && foundation === 'existing_content') {
      if (contentMode === 'draft' && !selectedDraftId) {
        showToast('Select an article from Racontera.', 'warning');
        return false;
      }
      if (contentMode === 'url' && !externalUrl.trim()) {
        showToast('Enter an article URL.', 'warning');
        return false;
      }
      return true;
    }
    if (s === 3 && foundation === 'brief' && saveRecurring.enabled) {
      const name = (saveRecurring.name || briefForm.name).trim();
      if (!name) {
        showToast('Name is required for saved research.', 'warning');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    if (step >= maxStep) return;
    if (step === 2 && foundation === 'existing_content') {
      setStep(3);
      return;
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step <= 1) return;
    setStep((s) => s - 1);
  };

  const handleRun = async () => {
    if (foundation === 'brief') {
      if (!validateStep(2) || !validateStep(3)) return;
    } else if (!validateStep(2)) {
      return;
    }
    try {
      setRunning(true);
      const payload = buildPayload();
      const response = await researchAPI.triggerWizard(payload);
      const reportId = response.data.report_id;
      if (!reportId) {
        showToast('Research started but no report ID returned.', 'warning');
        setRunning(false);
        return;
      }
      pollUntilReportHasIdeas(reportId, 'Research report ready');
    } catch (err) {
      setRunning(false);
      showToast(
        err.response?.data?.error || err.message || 'Failed to start research',
        'error'
      );
    }
  };

  const handleBriefChange = (e) => {
    const { name, value } = e.target;
    setBriefForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRunSaved = async (profileId) => {
    if (!window.confirm('Run research for this saved brief? This uses API credits.')) return;
    try {
      setTriggeringId(profileId);
      const res = await researchAPI.triggerResearch(profileId);
      const reportId = res.data.report_id;
      if (reportId) {
        pollUntilReportHasIdeas(reportId, 'Research report ready');
      }
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Run failed', 'error');
    } finally {
      setTriggeringId(null);
    }
  };

  const handleToggleAuto = async (profileId, enabled) => {
    const profile = savedProfiles.find((p) => p.id === profileId);
    if (!profile) return;

    const previous = savedProfiles;
    setSavedProfiles((list) =>
      list.map((p) => (p.id === profileId ? { ...p, auto_reports_enabled: enabled } : p))
    );

    try {
      await profilesAPI.updateProfile(profileId, {
        ...profile,
        auto_reports_enabled: enabled,
      });
      showToast(`Auto-run ${enabled ? 'enabled' : 'disabled'}`, 'success');
    } catch (err) {
      setSavedProfiles(previous);
      showToast(err.response?.data?.error || err.message || 'Update failed', 'error');
    }
  };

  const handleDeleteSaved = async (profile) => {
    if (!window.confirm(`Delete saved research "${profile.name}"?`)) return;
    try {
      await profilesAPI.deleteProfile(profile.id);
      showToast('Deleted.', 'success');
      loadSavedProfiles();
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Delete failed', 'error');
    }
  };

  const groupedSources = contentSources.reduce((acc, item) => {
    const g = item.group || 'workspace';
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

  const isReviewStep =
    (foundation === 'brief' && step === 4) ||
    (foundation === 'existing_content' && step === 3);

  const reviewSummary = () => {
    if (foundation === 'brief') {
      return (
        <ul className="rw-review-list">
          <li>
            <strong>Foundation:</strong> Research brief (broad)
          </li>
          {briefForm.name && (
            <li>
              <strong>Name:</strong> {briefForm.name}
            </li>
          )}
          {briefForm.target_keywords && (
            <li>
              <strong>Keywords:</strong> {briefForm.target_keywords}
            </li>
          )}
          {saveRecurring.enabled && (
            <li>
              <strong>Save:</strong> {saveRecurring.name || briefForm.name} (
              {saveRecurring.research_frequency}
              {saveRecurring.auto_reports_enabled ? ', auto-run on' : ''})
            </li>
          )}
        </ul>
      );
    }
    const draft = contentSources.find((d) => String(d.id) === String(selectedDraftId));
    return (
      <ul className="rw-review-list">
        <li>
          <strong>Foundation:</strong> Existing content (cluster ideas)
        </li>
        {contentMode === 'draft' && draft && (
          <li>
            <strong>Article:</strong> {draft.title}
          </li>
        )}
        {contentMode === 'url' && (
          <li>
            <strong>URL:</strong> {externalUrl}
          </li>
        )}
      </ul>
    );
  };

  return (
    <div className="rw-panel">
      <div className="rw-panel-inner">
        <div>
          <h3 className="rw-title">What should I write about?</h3>
          <p className="rw-lead">
            Start new research from a brief or from an existing article to discover cluster
            opportunities.
          </p>
        </div>
        <div className="rw-panel-actions">
          <button type="button" className="pf-btn pf-btn-secondary rw-btn-sm" onClick={openManage}>
            Manage saved
          </button>
          <button
            type="button"
            onClick={openWizard}
            className="pf-btn pf-btn-accent"
            disabled={running}
          >
            {running ? 'Research running…' : 'New research…'}
          </button>
        </div>
      </div>

      {open && (
        <div className="pf-modal-backdrop" role="presentation">
          <div
            className="pf-modal-panel rw-modal"
            role="dialog"
            aria-labelledby="research-wizard-title"
            aria-modal="true"
          >
            {view === 'manage' ? (
              <>
                <div className="pf-modal-head">
                  <h3 id="research-wizard-title">Saved research</h3>
                  <button type="button" className="pf-modal-close" aria-label="Close" onClick={closeModal}>
                    ×
                  </button>
                </div>
                <p className="pf-modal-lead">
                  Recurring research briefs run on a schedule when auto-run is enabled.
                </p>
                {manageLoading ? (
                  <p className="muted">Loading…</p>
                ) : savedProfiles.length === 0 ? (
                  <p className="muted">No saved research yet. Create one from the wizard.</p>
                ) : (
                  <div className="rw-saved-table-wrap">
                    <table className="rw-saved-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Schedule</th>
                          <th>Auto</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savedProfiles.map((p) => (
                          <tr key={p.id}>
                            <td>{p.name}</td>
                            <td>{p.research_frequency || 'weekly'}</td>
                            <td>
                              <label className="rw-auto-toggle-label">
                                <input
                                  type="checkbox"
                                  checked={p.auto_reports_enabled !== false}
                                  onChange={(e) =>
                                    void handleToggleAuto(p.id, e.target.checked)
                                  }
                                  aria-label={`Auto-run for ${p.name}`}
                                />
                                <span>{p.auto_reports_enabled !== false ? 'On' : 'Off'}</span>
                              </label>
                            </td>
                            <td className="rw-saved-actions">
                              <button
                                type="button"
                                className="pf-btn pf-btn-secondary rw-btn-sm"
                                disabled={triggeringId === p.id}
                                onClick={() => void handleRunSaved(p.id)}
                              >
                                Run
                              </button>
                              <button
                                type="button"
                                className="pf-btn pf-btn-inline-danger rw-btn-sm"
                                onClick={() => void handleDeleteSaved(p)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="pf-modal-actions">
                  <button type="button" className="pf-btn pf-btn-secondary" onClick={() => setView('wizard')}>
                    New research
                  </button>
                  <button type="button" className="pf-btn pf-btn-primary" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="pf-modal-head">
                  <h3 id="research-wizard-title">New research</h3>
                  <button type="button" className="pf-modal-close" aria-label="Close" onClick={closeModal}>
                    ×
                  </button>
                </div>
                <div className="rw-steps" aria-hidden="true">
                  Step {step} of {maxStep}
                </div>

                {step === 1 && (
                  <div className="rw-step">
                    <p className="pf-modal-lead">Choose your research foundation.</p>
                    <div className="rw-foundation-cards">
                      <label className={`rw-card ${foundation === 'brief' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="foundation"
                          value="brief"
                          checked={foundation === 'brief'}
                          onChange={() => setFoundation('brief')}
                        />
                        <span className="rw-card-title">Research brief</span>
                        <span className="rw-card-desc">
                          Broad research — trends, gaps, and varied ideas from your topic context.
                        </span>
                      </label>
                      <label className={`rw-card ${foundation === 'existing_content' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="foundation"
                          value="existing_content"
                          checked={foundation === 'existing_content'}
                          onChange={() => setFoundation('existing_content')}
                        />
                        <span className="rw-card-title">Existing content</span>
                        <span className="rw-card-desc">
                          Analyze an article for supplemental cluster spin-off ideas.
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {step === 2 && foundation === 'brief' && (
                  <div className="rw-step">
                    <div className="form-group">
                      <label htmlFor="rw-name">Name / topic label</label>
                      <input id="rw-name" name="name" value={briefForm.name} onChange={handleBriefChange} placeholder="e.g. SMB AI adoption" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="rw-niche">Niche / industry</label>
                      <input id="rw-niche" name="niche" value={briefForm.niche} onChange={handleBriefChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="rw-desc">Audience &amp; context</label>
                      <textarea id="rw-desc" name="description" rows={3} value={briefForm.description} onChange={handleBriefChange} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="rw-kw">Target keywords</label>
                      <input id="rw-kw" name="target_keywords" value={briefForm.target_keywords} onChange={handleBriefChange} placeholder="comma-separated" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="rw-comp">Competitor URLs</label>
                      <textarea id="rw-comp" name="competitor_urls" rows={2} value={briefForm.competitor_urls} onChange={handleBriefChange} placeholder="One per line or comma-separated" />
                    </div>
                  </div>
                )}

                {step === 2 && foundation === 'existing_content' && (
                  <div className="rw-step">
                    <div className="rw-content-tabs">
                      <button
                        type="button"
                        className={contentMode === 'draft' ? 'active' : ''}
                        onClick={() => setContentMode('draft')}
                      >
                        From Racontera
                      </button>
                      <button
                        type="button"
                        className={contentMode === 'url' ? 'active' : ''}
                        onClick={() => setContentMode('url')}
                      >
                        Enter URL
                      </button>
                    </div>
                    {contentMode === 'draft' ? (
                      sourcesLoading ? (
                        <p className="muted">Loading articles…</p>
                      ) : contentSources.length === 0 ? (
                        <p className="muted">No articles in Racontera yet.</p>
                      ) : (
                        <div className="rw-source-list">
                          {Object.entries(groupedSources).map(([group, items]) => (
                            <div key={group} className="rw-source-group">
                              <h4>{GROUP_LABELS[group] || group}</h4>
                              {items.map((item) => (
                                <label key={item.id} className="rw-source-item">
                                  <input
                                    type="radio"
                                    name="draftSource"
                                    value={item.id}
                                    checked={String(selectedDraftId) === String(item.id)}
                                    onChange={() => setSelectedDraftId(String(item.id))}
                                  />
                                  <span>
                                    <strong>{item.title}</strong>
                                    {item.target_keyword && (
                                      <span className="rw-source-meta"> — {item.target_keyword}</span>
                                    )}
                                    {item.word_count > 0 && (
                                      <span className="rw-source-meta"> ({item.word_count} words)</span>
                                    )}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      <div className="form-group">
                        <label htmlFor="rw-url">Article URL</label>
                        <input
                          id="rw-url"
                          type="url"
                          value={externalUrl}
                          onChange={(e) => setExternalUrl(e.target.value)}
                          placeholder="https://example.com/your-article"
                        />
                        <small>We&apos;ll fetch and analyze the page content.</small>
                      </div>
                    )}
                  </div>
                )}

                {step === 3 && foundation === 'brief' && (
                  <div className="rw-step">
                    <label className="rw-check">
                      <input
                        type="checkbox"
                        checked={saveRecurring.enabled}
                        onChange={(e) =>
                          setSaveRecurring((s) => ({ ...s, enabled: e.target.checked }))
                        }
                      />
                      Save for recurring research
                    </label>
                    {saveRecurring.enabled && (
                      <>
                        <div className="form-group">
                          <label htmlFor="rw-save-name">Saved name</label>
                          <input
                            id="rw-save-name"
                            value={saveRecurring.name}
                            onChange={(e) =>
                              setSaveRecurring((s) => ({ ...s, name: e.target.value }))
                            }
                            placeholder={briefForm.name || 'My research brief'}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="rw-freq">Frequency</label>
                          <select
                            id="rw-freq"
                            value={saveRecurring.research_frequency}
                            onChange={(e) =>
                              setSaveRecurring((s) => ({
                                ...s,
                                research_frequency: e.target.value,
                              }))
                            }
                          >
                            <option value="weekly">Weekly</option>
                            <option value="daily">Daily</option>
                          </select>
                        </div>
                        <label className="rw-check">
                          <input
                            type="checkbox"
                            checked={saveRecurring.auto_reports_enabled}
                            onChange={(e) =>
                              setSaveRecurring((s) => ({
                                ...s,
                                auto_reports_enabled: e.target.checked,
                              }))
                            }
                          />
                          Auto-run on schedule
                        </label>
                      </>
                    )}
                    {!saveRecurring.enabled && (
                      <p className="muted">One-off run — no saved profile will be created.</p>
                    )}
                  </div>
                )}

                {isReviewStep && (
                  <div className="rw-step">
                    <p className="pf-modal-lead">Review and start research.</p>
                    {reviewSummary()}
                  </div>
                )}

                <div className="pf-modal-actions rw-modal-actions">
                  <button type="button" className="rw-link-btn" onClick={() => setView('manage')}>
                    Manage saved research
                  </button>
                  <div className="rw-nav-buttons">
                    {step > 1 && (
                      <button type="button" className="pf-btn pf-btn-secondary" onClick={handleBack} disabled={running}>
                        Back
                      </button>
                    )}
                    {!isReviewStep && (
                      <button type="button" className="pf-btn pf-btn-primary" onClick={handleNext}>
                        Next
                      </button>
                    )}
                    {isReviewStep && (
                      <button
                        type="button"
                        className="pf-btn pf-btn-primary"
                        onClick={() => void handleRun()}
                        disabled={running}
                      >
                        {running ? 'Running…' : 'Start research'}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ResearchWizard;
